import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, Radio, ListTodo, BookOpen, ChartGantt, Camera, Warehouse, ChartPie, Building2, Clock, Receipt, Users, Briefcase,
  LayoutGrid, Plus, ChevronLeft, ChevronRight, ChevronDown, X, Search, FileText, Coins, ReceiptText, ScanLine, Package, Truck,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth, isManager, isAccountant, isDesigner, isSubcontractor } from "@/context/AuthContext";
import { toast } from "sonner";
import { personColor, norm } from "@/lib/taskCalendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// 305: zúžené menu — název ikony hned po najetí myší. Dřív jen title, ten prohlížeč ukáže
// až asi po vteřině a v aplikaci spuštěné z ikony někdy vůbec. Popisek je v portálu (seznam se neořízne).
export function SideTip({ label, on = true, children }) {
  if (!on) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={12} data-testid="side-tip"
        className="rounded-[10px] bg-foreground text-background text-[13px] font-semibold px-2.5 py-1.5 shadow-lg motion-reduce:animate-none">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Levý sloupec na PC (298) podle laboratoře „Proyekty – Můj návrh“ (menu C, 16. 10.):
 * skleněná karta s mezerou od okraje, Vytvořit, Moduly, připnuté zakázky, Firma a dole člověk.
 * Plus u nadpisu skupiny otevře panel s vypínači (u zakázek s hledáním); nastavení si drží
 * každý sám u svého účtu (300). Zúžený sloupec = pruh s ikonami. Na telefonu je dál Menu B.
 */
const M = (key, to, label, icon, testid, extra = {}) => ({ key, to, label, icon, testid, ...extra });
const CLOSED = ["Dokončeno", "Fakturováno", "Záruka"];
const PIN_MAX = 6;
export const SIDE_W = 236, SIDE_W_SLIM = 68;

export function sideFor(user) {
  if (isAccountant(user)) {
    return { label: "Ekonomika", projects: false, custom: false, firma: [], mods: [
      M("eko", "/ekonomika", "Přehled", ChartPie, "nav-eco-overview", { end: true }),
      M("doklady", "/ekonomika/doklady", "Doklady", FileText, "nav-economics", { prefix: "/ekonomika/doklady" }),
      M("platby", "/ekonomika/platby", "Platby", Coins, "nav-payments", { prefix: "/ekonomika/platby" }),
      M("pokladna", "/ekonomika/pokladna", "Pokladna", Coins, "nav-cashbox", { prefix: "/ekonomika/pokladna" }),
      M("naskladneni", "/ekonomika/naskladneni", "Naskladnění", Warehouse, "nav-stock", { prefix: "/ekonomika/naskladneni" }),
      M("rezie", "/ekonomika/rezie", "Režie", Receipt, "nav-overhead", { prefix: "/ekonomika/rezie" }),
    ] };
  }
  if (isManager(user)) {
    return { label: "Moduly", projects: true, custom: true, mods: [
      M("prehled", "/", "Přehled", LayoutDashboard, "nav-dashboard", { end: true }),
      // 335: Tabuli vidí celé vedení včetně asistent/ky (kdo je teď v práci).
      M("tabule", "/tabule", "Tabule", Radio, "nav-liveboard"),
      M("ukoly", "/ukoly", "Úkoly", ListTodo, "nav-tasks"),
      M("denik", "/denik", "Deník", BookOpen, "nav-diary"),
      M("gantt", "/planovani", "Gantt", ChartGantt, "nav-planning"),
      M("galerie", "/galerie", "Galerie", Camera, "nav-gallery"),
      M("sklad", "/sklad", "Sklad", Warehouse, "nav-warehouse"),
      M("objednavky", "/objednavky", "Objednávky", Package, "nav-orders"),
      // Ekonomika = přehled a její záložky; Doklady mají vlastní řádek ve Firmě.
      M("ekonomika", "/ekonomika", "Ekonomika", ChartPie, "nav-eco-overview", { match: (p) => p.startsWith("/ekonomika") && !p.startsWith("/ekonomika/doklady") }),
      M("investori", "/investori", "Investoři", Building2, "nav-investors"),
      M("dodavatele", "/dodavatele", "Dodavatelé", Truck, "nav-suppliers", { prefix: "/dodavatele" }),
    ], firma: [
      M("dochazka", "/dochazka", "Docházka", Clock, "nav-attendance"),
      M("doklady", "/ekonomika/doklady", "Doklady", Receipt, "nav-economics", { prefix: "/ekonomika/doklady" }),
      M("tym", "/tym", "Tým", Users, "nav-team"),
    ] };
  }
  if (isDesigner(user)) {
    return { label: "Moduly", projects: true, custom: false, firma: [], mods: [
      M("prehled", "/", "Přehled", LayoutDashboard, "nav-dashboard", { end: true }),
      M("ukoly", "/ukoly", "Úkoly", ListTodo, "nav-tasks"),
      M("denik", "/denik", "Deník", BookOpen, "nav-diary"),
      M("gantt", "/planovani", "Gantt", ChartGantt, "nav-planning"),
    ] };
  }
  if (isSubcontractor(user)) {
    return { label: "Moduly", projects: false, custom: false, firma: [], mods: [
      M("prehled", "/", "Přehled", LayoutDashboard, "nav-dashboard", { end: true }),
      M("ukoly", "/ukoly", "Úkoly", ListTodo, "nav-tasks"),
      M("dochazka", "/dochazka", "Docházka", Clock, "nav-attendance"),
      M("faktury", "/moje-faktury", "Moje faktury", ReceiptText, "nav-my-invoices"),
    ] };
  }
  if (user?.role === "WAREHOUSE") {
    return { label: "Moduly", projects: false, custom: false, firma: [], mods: [
      M("prehled", "/", "Přehled", LayoutDashboard, "nav-dashboard", { end: true }),
      M("sklad", "/sklad", "Sklad", Warehouse, "nav-warehouse"),
      M("objednavky", "/objednavky", "Dodávky", Package, "nav-orders"),
      M("dochazka", "/dochazka", "Docházka", Clock, "nav-attendance"),
    ] };
  }
  return { label: "Moduly", projects: true, custom: false, firma: [], mods: [
    M("prehled", "/", "Přehled", LayoutDashboard, "nav-dashboard", { end: true }),
    M("dochazka", "/dochazka", "Docházka", Clock, "nav-attendance"),
    M("ukoly", "/ukoly", "Úkoly", ListTodo, "nav-tasks"),
    M("galerie", "/galerie", "Galerie", Camera, "nav-gallery"),
    M("objednavky", "/objednavky", "Dodávky", Package, "nav-orders"),
  ] };
}

// 300: nastavení drží účet (user.menu_prefs z /auth/me, ukládá PUT /auth/menu) — na každém počítači stejné.
// Co si někdo stihl nastavit jen v prohlížeči (298), se při prvním načtení přenese k účtu.
const LOCAL = (uid) => `pp-side-${uid || "x"}`;
const clean = (v) => ({ off: Array.isArray(v?.off) ? v.off : [], pinned: Array.isArray(v?.pinned) ? v.pinned : null });
function localPrefs(uid) {
  try { const v = JSON.parse(localStorage.getItem(LOCAL(uid)) || "null"); return v && (v.off?.length || Array.isArray(v.pinned)) ? clean(v) : null; }
  catch { return null; }
}

const isActive = (it, path) => (it.match ? it.match(path) : it.prefix ? path.startsWith(it.prefix) : it.end ? path === it.to : path === it.to || path.startsWith(`${it.to}/`));

function Toggle({ on }) {
  return (
    <span aria-hidden="true" className={`relative w-[34px] h-5 rounded-full shrink-0 transition-colors ${on ? "bg-brand" : "bg-foreground/15"}`}>
      <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-[left]" style={{ left: on ? 16 : 2 }} />
    </span>
  );
}

function PanelHead({ title, onClose }) {
  return (
    <div className="flex items-center justify-between gap-2 px-4 pt-3.5 pb-1">
      <b className="text-[15px] font-bold">{title}</b>
      <button type="button" onClick={onClose} aria-label="Zavřít" className="w-8 h-8 -mr-1.5 rounded-full grid place-items-center text-muted-foreground hover:bg-secondary"><X size={17} /></button>
    </div>
  );
}
const panelCls = "w-[280px] p-0 rounded-[16px] border-[color:var(--pp-glass-brd)] bg-popover/95 backdrop-blur-xl shadow-2xl overflow-hidden";
const doneBtn = "h-9 px-4 rounded-full bg-foreground text-background text-[13.5px] font-bold";

// Vypínače modulů (nebo Firmy): co člověk nepoužívá, z menu zmizí — stránka dál existuje.
function ItemsPanel({ title, items, off, onToggle, onClose }) {
  return (
    <>
      <PanelHead title={title} onClose={onClose} />
      <p className="px-4 pb-2 text-[13px] text-muted-foreground">Vypni, co nepoužíváš. Zapnout to jde kdykoli zase tady.</p>
      <div className="px-2 pb-2 max-h-[min(60vh,480px)] overflow-y-auto">
        {items.map((it) => (
          <button key={it.key} type="button" role="switch" aria-checked={!off.includes(it.key)} onClick={() => onToggle(it.key)} data-testid={`side-toggle-${it.key}`}
            className="w-full h-10 px-2 rounded-[10px] flex items-center gap-2.5 text-left text-sm font-medium hover:bg-secondary">
            <it.icon size={17} className="text-muted-foreground shrink-0" />
            <span className="flex-1 min-w-0 truncate">{it.label}</span>
            <Toggle on={!off.includes(it.key)} />
          </button>
        ))}
      </div>
      <div className="flex justify-end border-t border-border px-4 py-3"><button type="button" onClick={onClose} className={doneBtn}>Hotovo</button></div>
    </>
  );
}

// Připnuté zakázky: hledání, přepínač u každé aktivní zakázky, „Odepnout vše“.
function ProjectsPanel({ active, pinned, onToggle, onClear, onClose }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const n = norm(q.trim());
    const rows = n ? active.filter((p) => norm(`${p.name} ${p.code || ""} ${p.address || ""}`).includes(n)) : active;
    return [...rows].sort((a, b) => (pinned.includes(b.id) ? 1 : 0) - (pinned.includes(a.id) ? 1 : 0));
  }, [active, pinned, q]);
  return (
    <>
      <PanelHead title="Zakázky v menu" onClose={onClose} />
      <div className="px-4 pt-1 pb-2">
        <label className="flex items-center gap-2 h-10 px-3 rounded-[10px] border border-border bg-background/60 focus-within:border-brand">
          <Search size={16} className="text-muted-foreground shrink-0" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat název nebo číslo" className="min-w-0 flex-1 bg-transparent outline-none text-sm" data-testid="side-project-search" />
        </label>
        <div className="mt-2.5 text-xs font-semibold text-muted-foreground">V menu: <b className="text-foreground">{pinned.length}</b></div>
      </div>
      <div className="px-2 pb-2 max-h-[min(52vh,420px)] overflow-y-auto">
        {list.map((p) => (
          <button key={p.id} type="button" role="switch" aria-checked={pinned.includes(p.id)} onClick={() => onToggle(p.id)} data-testid={`side-pin-${p.id}`}
            className="w-full min-h-[44px] px-2 py-1.5 rounded-[10px] flex items-center gap-2.5 text-left hover:bg-secondary">
            <i aria-hidden="true" className="w-[13px] h-[13px] rounded-[4px] shrink-0" style={{ background: personColor(p.id) }} />
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium leading-snug">{p.name}</span>
              {(p.code || p.address) && <span className="block text-xs text-muted-foreground truncate">{[p.code, p.address].filter(Boolean).join(" · ")}</span>}
            </span>
            <Toggle on={pinned.includes(p.id)} />
          </button>
        ))}
        {!list.length && <div className="px-2 py-6 text-center text-sm text-muted-foreground">{active.length ? "Nic nenalezeno." : "Žádná rozpracovaná zakázka."}</div>}
      </div>
      <div className="flex items-center justify-between border-t border-border px-4 py-3">
        <button type="button" onClick={onClear} disabled={!pinned.length} className="text-[13px] font-semibold text-muted-foreground hover:text-foreground disabled:opacity-40">Odepnout vše</button>
        <button type="button" onClick={onClose} className={doneBtn}>Hotovo</button>
      </div>
    </>
  );
}

/**
 * props: user, company, slim, onToggleSlim, create [{key,label,sub,icon,onSelect}], footer (uzel: člověk + zvoneček)
 */
export function SideMenu({ user, company, slim, onToggleSlim, create = [], footer }) {
  const { pathname } = useLocation();
  const conf = useMemo(() => sideFor(user), [user]);
  const { setUser } = useAuth();
  const [prefs, setPrefs] = useState(() => (user?.menu_prefs ? clean(user.menu_prefs) : localPrefs(user?.id) || clean(null)));
  const dirty = useRef(!user?.menu_prefs && !!localPrefs(user?.id));   // přenést nastavení z prohlížeče k účtu
  const [panel, setPanel] = useState(null);   // "mods" | "firma" | "prj"
  const [more, setMore] = useState(false);
  const setP = (fn) => { dirty.current = true; setPrefs(fn); };
  // Uloží se chvíli po posledním kliknutí (víc vypínačů za sebou = jeden požadavek).
  useEffect(() => {
    if (!dirty.current) return undefined;
    const t = setTimeout(() => {
      dirty.current = false;
      api.put("/auth/menu", prefs)
        .then(({ data }) => {
          setUser((u) => (u ? { ...u, menu_prefs: data } : u));
          try { localStorage.removeItem(LOCAL(user?.id)); } catch { /* bez úložiště */ }
        })
        .catch(() => toast.error("Nastavení menu se nepodařilo uložit. Zkus to prosím znovu."));
    }, 600);
    return () => clearTimeout(t);
  }, [prefs]); // eslint-disable-line react-hooks/exhaustive-deps

  const projects = useQuery({ queryKey: ["projects-options"], queryFn: () => api.get("/projects/options").then((r) => r.data),
    enabled: !!conf.projects, staleTime: 5 * 60_000 }).data;
  const active = useMemo(() => (projects || []).filter((p) => !CLOSED.includes(p.status)), [projects]);
  // Bez vlastního výběru jsou připnuté čtyři nejnovější rozpracované zakázky (jako v laboratoři).
  const pinned = useMemo(() => (prefs.pinned ?? active.slice(0, 4).map((p) => p.id)).filter((id) => active.some((p) => p.id === id)), [prefs.pinned, active]);
  const pins = pinned.map((id) => active.find((p) => p.id === id));
  const shownPins = more ? pins : pins.slice(0, PIN_MAX);

  const mods = conf.mods.filter((m) => !conf.custom || !prefs.off.includes(m.key));
  const firma = conf.firma.filter((m) => !conf.custom || !prefs.off.includes(m.key));
  const togOff = (k) => setP((p) => ({ ...p, off: p.off.includes(k) ? p.off.filter((x) => x !== k) : [...p.off, k] }));
  const togPin = (id) => setP((p) => { const cur = p.pinned ?? pinned; return { ...p, pinned: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] }; });

  const link = (it, extra = "") => {
    const on = isActive(it, pathname);
    return (
      <li key={`${it.key}-${it.to}`}>
        <SideTip label={it.label} on={slim}>
        <Link to={it.to} data-testid={it.testid} aria-current={on ? "page" : undefined}
          className={`pp-side-link ${on ? "is-active" : ""} flex items-center rounded-[10px] text-sm font-medium transition-colors ${slim ? "relative w-11 h-11 mx-auto justify-center rounded-[12px]" : "h-[34px] gap-2.5 px-2.5"} ${extra}`}>
          {it.dot ? <i aria-hidden="true" className="w-[13px] h-[13px] rounded-[4px] shrink-0 mx-0.5" style={{ background: it.dot }} />
            : <it.icon size={slim ? 20 : 17} strokeWidth={2} className="shrink-0" />}
          {slim ? <span className="sr-only">{it.label}</span> : <span className="min-w-0 flex-1 truncate">{it.label}</span>}
          {!slim && it.count != null && <em className="not-italic text-xs font-semibold text-[color:var(--pp-ink-2)] tabular-nums">{it.count}</em>}
        </Link>
        </SideTip>
      </li>
    );
  };
  const head = (label, key) => !slim && (
    <div className="flex items-center justify-between pr-1">
      <p className="px-2.5 pt-3 pb-1 text-xs font-semibold text-[color:var(--pp-ink-2)]">{label}</p>
      {key && (
        <Popover open={panel === key} onOpenChange={(o) => setPanel(o ? key : null)}>
          <PopoverTrigger asChild>
            <button type="button" aria-label={`Upravit skupinu ${label}`} title="Upravit, co je v menu" data-testid={`side-edit-${key}`}
              className="mt-1.5 w-[26px] h-[26px] rounded-[8px] grid place-items-center text-[color:var(--pp-ink-2)] hover:bg-foreground/5 hover:text-foreground"><Plus size={16} /></button>
          </PopoverTrigger>
          <PopoverContent side="right" align="start" sideOffset={14} className={panelCls} onOpenAutoFocus={(e) => key !== "prj" && e.preventDefault()}>
            {key === "prj"
              ? <ProjectsPanel active={active} pinned={pinned} onToggle={togPin} onClear={() => setP((p) => ({ ...p, pinned: [] }))} onClose={() => setPanel(null)} />
              : <ItemsPanel title={key === "mods" ? "Moduly v menu" : "Firma v menu"} items={key === "mods" ? conf.mods : conf.firma} off={prefs.off} onToggle={togOff} onClose={() => setPanel(null)} />}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );

  const createBtn = (
    <button type="button" data-testid="side-create-btn" aria-label="Vytvořit" title={slim ? "Vytvořit" : undefined}
      className={`pp-create shrink-0 flex items-center justify-center gap-2 font-semibold text-sm ${slim ? "w-11 h-11 mx-auto mb-3 rounded-full" : "h-[35px] mx-1 mb-2.5 rounded-full px-4"}`}>
      <Plus size={slim ? 20 : 17} strokeWidth={2.4} />{!slim && <span>Vytvořit</span>}
    </button>
  );

  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={300}>
    <aside aria-label="Hlavní menu" data-testid="side-menu"
      className="pp-side hidden md:flex md:flex-col fixed z-30 top-3 bottom-3 left-3 rounded-[20px] transition-[width] duration-200 motion-reduce:transition-none"
      style={{ width: slim ? SIDE_W_SLIM : SIDE_W, padding: slim ? "12px 10px" : "14px 10px 12px" }}>
      <SideTip label={slim ? "Rozšířit menu" : "Zúžit menu"}>
        <button type="button" onClick={onToggleSlim} data-testid="sidebar-collapse-btn" aria-expanded={!slim}
          aria-label={slim ? "Rozšířit menu" : "Zúžit menu"}
          className="pp-glass-btn absolute -right-3.5 top-[22px] w-7 h-7 rounded-full grid place-items-center hover:text-foreground z-10">
          {slim ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
        </button>
      </SideTip>
      <div className={`flex items-center gap-2.5 shrink-0 ${slim ? "justify-center pb-3" : "px-1.5 pb-3.5"}`}>
        {/* 301: skutečné logo Proyekty (v laboratoři bylo jen písmeno P) */}
        <img src="/logo-192.png" alt="" aria-hidden="true" className="w-9 h-9 shrink-0" data-testid="sidebar-logo" />
        {!slim && (
          <span className="min-w-0">
            <b className="block text-[17px] font-extrabold tracking-tight leading-tight">Proyekty</b>
            <small className="block text-xs font-medium text-[color:var(--pp-ink-2)] truncate">{company?.name}</small>
          </span>
        )}
      </div>
      {create.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{createBtn}</DropdownMenuTrigger>
          <DropdownMenuContent side={slim ? "right" : "bottom"} align="start" sideOffset={slim ? 14 : 6} className="w-[300px] p-1.5 rounded-[16px]" data-testid="side-create-menu">
            <DropdownMenuLabel className="px-3 pt-2 pb-1 text-xs font-semibold text-muted-foreground">Vytvořit</DropdownMenuLabel>
            {create.map((c) => (
              <DropdownMenuItem key={c.key} onSelect={c.onSelect} data-testid={`side-create-${c.key}`} className="gap-3 px-3 py-2 rounded-[10px] items-start">
                <c.icon size={18} className="mt-0.5 text-muted-foreground shrink-0" />
                <span className="min-w-0"><span className="block text-sm font-semibold">{c.label}</span><span className="block text-xs text-muted-foreground">{c.sub}</span></span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <nav aria-label="Sekce" className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden ${slim ? "-mx-1 px-1" : "-mx-1 px-1"}`}>
        {head(conf.label, conf.custom ? "mods" : null)}
        <ul className={`grid grid-cols-1 ${slim ? "gap-1" : "gap-px"}`}>{mods.map((it) => link(it))}</ul>
        {conf.projects && (
          <>
            {head("Zakázky", "prj")}
            {!slim ? (
              <ul className="grid grid-cols-1 gap-px">
                {shownPins.map((p) => link({ key: `p-${p.id}`, to: `/zakazky/${p.id}`, label: p.name, dot: personColor(p.id), testid: `side-project-${p.id}` }))}
                {pins.length > PIN_MAX && (
                  <li><button type="button" onClick={() => setMore((m) => !m)} aria-expanded={more}
                    className="pp-side-link w-full h-[34px] px-2.5 flex items-center gap-2.5 rounded-[10px] text-sm font-medium">
                    <ChevronDown size={17} className={more ? "rotate-180" : ""} /><span>{more ? "Méně" : `Další ${pins.length - PIN_MAX}`}</span></button></li>
                )}
                {link(M("zakazky", "/zakazky", "Všechny zakázky", LayoutGrid, "nav-projects", { count: projects ? active.length : null,
                  match: (p) => p === "/zakazky" || (p.startsWith("/zakazky/") && !pinned.some((id) => p.startsWith(`/zakazky/${id}`))) }))}
              </ul>
            ) : (
              <ul className="grid grid-cols-1 gap-1 mt-1">{link(M("zakazky", "/zakazky", "Zakázky", Briefcase, "nav-projects"))}</ul>
            )}
          </>
        )}
        {firma.length > 0 && (
          <>
            {head("Firma", conf.custom ? "firma" : null)}
            <ul className={`grid grid-cols-1 ${slim ? "gap-1 mt-1" : "gap-px"}`}>{firma.map((it) => link(it))}</ul>
          </>
        )}
      </nav>
      <div className={`shrink-0 border-t border-foreground/10 mt-2 ${slim ? "pt-2.5" : "pt-2.5 px-0.5"}`}>{footer}</div>
    </aside>
    </TooltipProvider>
  );
}

export const CREATE_ICONS = { task: ListTodo, diary: BookOpen, photos: Camera, doc: ScanLine, project: Briefcase };
